import React, { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  Box,
  Heading,
  Image,
  Link,
  NeoColor,
  Spacing,
  Stack,
  StackItem,
  Text,
} from "@noom/wax-component-library";

import { NeoButton } from "@/components";
import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { useGoto, useTrackEvent } from "@/hooks";
import { useBWell } from "@/hooks/useBWell";
import { captureException } from "@/utils/sentry";

import { BaseInterstitial } from "../core";

const QUESTION_ID = "connectHealthRecordsConsent";

const ConnectHealthRecordsConsent: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
}) => {
  const { t } = useTranslation();
  const { isLoading, createIasConsent } = useBWell();
  const goto = useGoto();
  const { trackIdentifiedPageViewed } = useTrackEvent();

  useEffect(() => {
    trackIdentifiedPageViewed(QUESTION_ID);
  }, []);

  const handleClickNext = async () => {
    try {
      await createIasConsent();
      onClickNext(null);
    } catch (error) {
      captureException(error);
      goto.error();
    }
  };

  const bulletItems = t("connectHealthRecordsConsent.bullets", {
    returnObjects: true,
  }) as string[];

  return (
    <BaseInterstitial
      onClickBack={onClickBack}
      onClickNext={onClickNext}
      questionId={QUESTION_ID}
    >
      <Stack spacing={Spacing[4]} marginTop={Spacing[4]}>
        <Heading fontSize="24px" fontWeight={500} lineHeight="32px">
          {t("connectHealthRecordsConsent.heading")}
        </Heading>
        <Text>{t("connectHealthRecordsConsent.subheading")}</Text>
      </Stack>

      <Box
        display="flex"
        alignItems="flex-start"
        backgroundColor={NeoColor.blue.skyLight}
        borderRadius="8px"
        gap={Spacing[3]}
        padding={Spacing[3]}
      >
        <Stack spacing={Spacing[0]} alignItems="flex-start" flex={1}>
          <Text textTransform="uppercase" fontSize="xs" fontWeight="500">
            {t("connectHealthRecordsConsent.privacyLabel")}
          </Text>
          <Text fontSize="sm">
            {t("connectHealthRecordsConsent.privacyText")}
          </Text>
        </Stack>
        <Image
          alt="Privacy shield"
          src="/assets/img/shield-check-teal.svg"
          flexShrink={0}
        />
      </Box>

      <Stack
        spacing={Spacing[4]}
        padding={Spacing[4]}
        borderRadius={12}
        border="1px solid #F0F0F0"
        boxShadow="md"
      >
        <Text marginBottom={Spacing[3]}>
          {t("connectHealthRecordsConsent.authorizationLabel")}
        </Text>
        <Stack spacing={Spacing[3]} alignItems="stretch">
          {bulletItems.map((key) => (
            <Box
              key={key}
              display="flex"
              gap={Spacing[2]}
              alignItems="flex-start"
            >
              <Image
                alt="Diamond bullet"
                marginTop="4px"
                src="/assets/img/diamond-tarocco.svg"
                flexShrink={0}
              />
              <Text>{key}</Text>
            </Box>
          ))}
        </Stack>
      </Stack>

      <Box textAlign="center">
        <Text size="sm">
          <Trans
            i18nKey="connectHealthRecordsConsent.footerText"
            components={[
              <Text key="prefix" as="span" />,
              <Link
                color={NeoColor.blue.lagoon}
                href="/assets/files/bwell-terms.pdf"
                isExternal
                key="link"
              />,
            ]}
          />
        </Text>
      </Box>

      <StackItem display="flex" justifyContent="center">
        <NeoButton
          onClick={handleClickNext}
          isLoading={isLoading}
          disabled={isLoading}
        >
          {t("connectHealthRecordsConsent.ctaText")}
        </NeoButton>
      </StackItem>
    </BaseInterstitial>
  );
};

export const Q_CONNECT_HEALTH_RECORDS_CONSENT: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: () => true,
  component: ConnectHealthRecordsConsent,
};
